import { inject, injectable } from 'inversify';
import type { EndPhases } from './interfaces';
import { PhaseTypes } from './interfaces';
import type { GraphConfigProperties, IPhasesCurveController, PhaseData } from './interfaces';
import type { ViewsService } from '@/engine/app/Views.service';
import { TYPES } from '@/engine/ioc/types';
import type { CasePhaseData } from '@/engine/app/CasePhaseData/CasePhaseData.ts';
import { TYPES as TYPES2 } from '@/engine/app/modules/images/iocTypes';
import type { ImagesService } from '@/engine/app/modules/images/Images.service';
import type { AppController } from '@/engine/app/App.controller';
import type { IPhaseInfo } from '@/engine/app/CasePhaseData/interfaces.ts';
import type { IBaseViewModel, IViewItemsViewModelController } from '@/engine/app/interfaces.ts';
import type { ViewEntity } from '@/engine/app/models/viewEntity/view.entity.ts';
import { CORE_TYPES } from '@/engine/app/modules/core';
import type { ICommandProcessor } from '@/engine/app/modules/core/interfaces/Command';

@injectable()
export class PhasesCurveController implements IPhasesCurveController {
  private readonly _appController: AppController;

  private readonly _viewsService: ViewsService;

  private readonly _casePhaseData: CasePhaseData;

  private readonly _imagesService: ImagesService;

  private readonly _viVmController: IViewItemsViewModelController;

  private readonly _commandProcessor: ICommandProcessor;

  private _undoSystoleIndex: number | undefined;

  private _undoDiastoleIndex: number | undefined;

  public constructor(
    @inject(TYPES.AppController) appController: AppController,
    @inject(TYPES.ViewsService) viewsService: ViewsService,
    @inject(TYPES.CasePhaseData) casePhaseData: CasePhaseData,
    @inject(TYPES2.ImagesService) imagesService: ImagesService,
    @inject(TYPES.IViewItemsViewModelController) viVmController: IViewItemsViewModelController,
    @inject(CORE_TYPES.CommandProcessor) commandProcessor: ICommandProcessor
  ) {
    this._appController = appController;
    this._viewsService = viewsService;
    this._casePhaseData = casePhaseData;
    this._imagesService = imagesService;
    this._viVmController = viVmController;
    this._commandProcessor = commandProcessor;
  }

  public getData(props: GraphConfigProperties): [Array<PhaseData>, EndPhases | undefined] {
    const currentView = this._viewsService.currentView;
    if (!currentView) {
      throw new Error('Current view have to be defined');
    }

    const imageStackDescriptor = currentView.getImageStackDescriptor();
    const availablePhasesInfos: Array<IPhaseInfo> = this._casePhaseData.getAvailablePhasesInfos(imageStackDescriptor);

    const endSystoleIndex = this._casePhaseData.getParam(imageStackDescriptor, 'endSystoleIndex');
    const endDiastoleIndex = this._casePhaseData.getParam(imageStackDescriptor, 'endDiastoleIndex');

    const data = availablePhasesInfos.reduce((acc: Array<PhaseData>, phaseInfo, currentIndex) => {
      const phasePercentage = phaseInfo.getParam('weight');
      const phaseWeight = phaseInfo.getParam(props.paramName) ?? 0;
      if (phasePercentage !== undefined && endSystoleIndex !== undefined && endDiastoleIndex !== undefined) {
        acc.push({
          phase: phasePercentage,
          weight: phaseWeight * props.factor,
          type: this._getPhaseTypeByIndexes(currentIndex, endSystoleIndex, endDiastoleIndex)
        });
      }
      return acc;
    }, []);

    const endPhases =
      endSystoleIndex === undefined ||
      endDiastoleIndex === undefined ||
      data[endSystoleIndex] === undefined ||
      data[endDiastoleIndex] === undefined
        ? undefined
        : {
            [PhaseTypes.Systole]: { phase: data[endSystoleIndex].phase, index: endSystoleIndex },
            [PhaseTypes.Diastole]: { phase: data[endDiastoleIndex].phase, index: endDiastoleIndex }
          };
    return [data, endPhases];
  }

  public getPhasesLength(): number {
    const currentView = this._viewsService.currentView;
    if (!currentView) {
      throw new Error('Current view have to be defined');
    }
    return this._casePhaseData.getAvailablePhases(currentView.getImageStackDescriptor()).length;
  }

  private _getPhaseTypeByIndexes(phaseIndex: number, endSystoleIndex: number, endDiastoleIndex: number): PhaseTypes {
    const lowerBoundaryIndex = Math.min(endSystoleIndex, endDiastoleIndex);
    const higherBoundaryIndex = Math.max(endSystoleIndex, endDiastoleIndex);

    const lowerBoundaryType: PhaseTypes =
      lowerBoundaryIndex === endSystoleIndex ? PhaseTypes.Systole : PhaseTypes.Diastole;
    const higherBoundaryType: PhaseTypes =
      higherBoundaryIndex === endDiastoleIndex ? PhaseTypes.Diastole : PhaseTypes.Systole;

    if (phaseIndex <= lowerBoundaryIndex) {
      return lowerBoundaryType;
    } else if (phaseIndex <= higherBoundaryIndex) {
      return higherBoundaryType;
    } else {
      return lowerBoundaryType;
    }
  }

  public get isImagesLoadingCompleted(): boolean {
    return this._imagesService.isImagesLoadingCompleted;
  }

  public get isCineloopActive(): boolean {
    return this._appController.isCineloopActive;
  }

  public get isCineloopAvailable(): boolean {
    return this._imagesService.isCineloopAvailable;
  }

  public get currentPhasePercentage(): number | undefined {
    const currentView = this._viewsService.currentView;
    if (!currentView) {
      throw new Error('Current view have to be defined');
    }
    const activePhaseId = this._casePhaseData.getActivePhase(currentView.getImageStackDescriptor()).id;

    const activePhaseInfo = this._casePhaseData
      .getAvailablePhasesInfos(currentView.getImageStackDescriptor())
      .find((val) => val.id == activePhaseId);

    return activePhaseInfo?.getParam('weight');
  }

  public setEndPositions(endPhases: EndPhases): void {
    if (endPhases[PhaseTypes.Systole].index === endPhases[PhaseTypes.Diastole].index) {
      return;
    }

    const currentView = this._viewsService.currentView;
    if (!currentView) {
      throw new Error('Current view have to be defined');
    }
    const imageStackDescriptor = currentView.getImageStackDescriptor();

    this._casePhaseData.setParam(imageStackDescriptor, 'endSystoleIndex', endPhases[PhaseTypes.Systole].index);
    this._casePhaseData.setParam(imageStackDescriptor, 'endDiastoleIndex', endPhases[PhaseTypes.Diastole].index);
  }

  // eslint-disable-next-line @typescript-eslint/no-misused-promises -- //TODO: [chebotarev] fix eslint no-misused-promises
  public async finalizeEndPositions(): Promise<void> {
    const imageStackDescriptor = this._viewsService.currentViewOrThrow.getImageStackDescriptor();
    const redoSystoleIndex = this._casePhaseData.getParam(imageStackDescriptor, 'endSystoleIndex');
    const redoDiastoleIndex = this._casePhaseData.getParam(imageStackDescriptor, 'endDiastoleIndex');
    const undoSystoleIndex = this._undoSystoleIndex;
    const undoDiastoleIndex = this._undoDiastoleIndex;

    if (
      undoSystoleIndex === undefined ||
      undoDiastoleIndex === undefined ||
      redoSystoleIndex === undefined ||
      redoDiastoleIndex === undefined
    ) {
      return;
    }
    if (undoSystoleIndex === redoSystoleIndex && undoDiastoleIndex === redoDiastoleIndex) {
      return;
    }

    try {
      await this._commandProcessor.executeAnonymousCommand(
        (): Promise<undefined> => {
          this._casePhaseData.setParam(imageStackDescriptor, 'endSystoleIndex', redoSystoleIndex);
          this._casePhaseData.setParam(imageStackDescriptor, 'endDiastoleIndex', redoDiastoleIndex);
          return Promise.resolve(undefined);
        },
        (): Promise<undefined> => {
          this._casePhaseData.setParam(imageStackDescriptor, 'endSystoleIndex', undoSystoleIndex);
          this._casePhaseData.setParam(imageStackDescriptor, 'endDiastoleIndex', undoDiastoleIndex);
          return Promise.resolve(undefined);
        }
      );
    } catch (error: unknown) {
      console.warn(error);
      throw new Error('End position finalization failed!');
    }
  }

  public initializeEndPositions(): void {
    const imageStackDescriptor = this._viewsService.currentViewOrThrow.getImageStackDescriptor();
    this._undoSystoleIndex = this._casePhaseData.getParam(imageStackDescriptor, 'endSystoleIndex');
    this._undoDiastoleIndex = this._casePhaseData.getParam(imageStackDescriptor, 'endDiastoleIndex');
  }

  public async setPhase(index: number): Promise<void> {
    if (!this.isImagesLoadingCompleted) return;

    const currentView: ViewEntity | null = this._viewsService.currentView;

    if (!currentView) throw new Error('Current view is not defined');
    const imageStackDescriptor = currentView.getImageStackDescriptor();
    const undoPhaseIndex = this._casePhaseData.getActivePhaseIdx(imageStackDescriptor);
    const redoPhaseIndex = index;

    try {
      await this._commandProcessor.executeAnonymousCommand(
        async (): Promise<undefined> => {
          await this._appController.changePhaseAsync(imageStackDescriptor, redoPhaseIndex);
        },
        async (): Promise<undefined> => {
          await this._appController.changePhaseAsync(imageStackDescriptor, undoPhaseIndex);
        }
      );
    } finally {
      this._updateViewModelsSelection();
    }
  }

  private _updateViewModelsSelection(): void {
    const selectedViewModels: Array<IBaseViewModel> = this._viVmController
      .getActualViewModels()
      .filter((vm: IBaseViewModel) => vm.isSelected);

    if (selectedViewModels.length == 0) return;

    const [selectedViewModel, dependentViewModel] = selectedViewModels;
    const attachedViewModel =
      dependentViewModel?.descriptor === selectedViewModel?.attachments[0] ? dependentViewModel : undefined;

    this._viVmController.setViewModelSelection(
      selectedViewModel.descriptor,
      selectedViewModel.isSelected,
      attachedViewModel
    );
  }
}
